<template>
  <div v-if="profile.id">
    <v-list v-if="!loading && documents" two-line ref="list">
      <template v-for="(document, i) in documents">
        <v-subheader v-if="document.header" :key="`${i}-header`">
          {{ document.header }}
        </v-subheader>

        <v-divider
          v-else-if="document.divider"
          :inset="document.inset"
          :key="`${i}-divider`"
        />

        <v-list-tile v-else :key="`${i}-title`">
          <v-list-tile-avatar>
            <Flag icon v-if="document.flag" :value="document.flag" />
            <v-icon v-else>{{ document.icon }}</v-icon>
          </v-list-tile-avatar>

          <v-list-tile-content v-if="document.title">
            <v-list-tile-title>
              <DocumentUploadButton
                v-if="document.file && document.file.url"
                :label="document.title"
                :refresh="document.api.get"
                :post="document.api.post"
                :file="document.file"
                @done="initialize"
                :clearable="document.clearable"
              >
                <a
                  slot="activator"
                  slot-scope="{ trigger }"
                  :class="themes.candidate.light.primaryText"
                  @click="trigger"
                  ref="replace"
                >
                  {{ document.title }}
                </a>
              </DocumentUploadButton>
              <span v-else>
                {{ document.title }}
              </span>
            </v-list-tile-title>
            <v-list-tile-sub-title>
              {{ document.subtitle }}
            </v-list-tile-sub-title>
          </v-list-tile-content>

          <v-list-tile-action>
            <v-btn
              v-if="document.file && document.file.url"
              :color="themes.candidate.light.successButton"
              @click.stop=""
              round
              icon
            >
              <v-icon>mdi-check</v-icon>
            </v-btn>
            <DocumentUploadButton
              v-else
              label="Identification Document"
              :refresh="document.api.get"
              :post="document.api.post"
              :file="document.file"
              @done="initialize"
            >
              <v-btn
                slot="activator"
                slot-scope="{ trigger, uploading }"
                :class="themes.candidate.light.primaryActionButton"
                @click.stop="trigger"
                :loading="uploading"
                ref="add"
                round
                icon
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </DocumentUploadButton>
          </v-list-tile-action>
        </v-list-tile>
      </template>
    </v-list>
  </div>
</template>

<script>
// Components
import DocumentUploadButton from "@/components/candidate/DocumentUploadButton";
import Flag from "@/components/candidate/flags/Flag";

// Helpers
import { mapGetters } from "vuex";
import _ from "lodash";
import { byEndsAt, byLevels } from "@/sorting/ProfileQualification";
import { byGradeValue } from "@/sorting/QualificationComponents";
import { mode } from "simple-statistics";

export default {
  components: { DocumentUploadButton, Flag },

  created() {
    Promise.all([
      this.$store.dispatch("candidate/profileQualifications/fetchAll"),
    ])
      .then(this.initialize)
      .catch(() => {})
      .finally(() => (this.loading = false));
  },

  props: { profile: { type: Object, required: true } },

  data() {
    return {
      loading: true,
      documents: [],
    };
  },

  computed: {
    ...mapGetters({
      profileQualifications: "candidate/profileQualifications/all",
    }),

    flags() {
      if (!_.get(this, "profile.flags[0]")) return [];

      return _.get(this, "profile.flags");
    },

    qualifications() {
      if (!_.get(this, "profileQualifications.length")) return [];

      let qualifications = JSON.parse(
        JSON.stringify(this.profileQualifications)
      );
      return qualifications
        .sort(byLevels({ country: this.levelsCountryMode(), asc: false }))
        .sort(byEndsAt({ asc: false }));
    },
  },

  watch: {
    "$route.hash": {
      handler(hash) {
        if (hash === "#documents") this.initialize();
      },
    },
  },

  methods: {
    initialize() {
      this.documents = [];

      const id = _.get(this, "profile.identityDocument");
      this.documents.push(
        { header: "Personal Documents" },
        {
          icon: id ? "mdi-file" : "mdi-file-outline",
          color: id
            ? this.themes.candidate.light.success
            : this.themes.candidate.light.warning,
          title: "National Identification Document",
          subtitle: id
            ? "Succesfully Uploaded"
            : "Provide your national identification or passport document",
          file: id,
          api: {
            post: "/candidate/profile/identity_document",
            get: "candidate/profile/fetch",
          },
          flag: this.flags.find((f) =>
            RegExp("identity_document", "gi").test(f.key)
          ),
          clearable: false,
        }
      );

      if (this.qualifications && this.qualifications[0]) {
        this.documents.push({ header: "Qualification Documents" });
      }

      for (const q of this.qualifications) {
        // Transcripts
        if (
          this.moreThanSixMonthsAgo(q.startsAt) ||
          (q.gradeValue && q.gradeValue.length)
        ) {
          let transcript = _.get(q, "transcript");

          this.documents.push({
            icon:
              transcript || q.accessRestricted
                ? "mdi-file"
                : "mdi-file-outline",
            color: transcript
              ? this.themes.candidate.light.success
              : this.themes.candidate.light.warning,
            title: `${q.qualificationTypeName} ${q.specialization}`,
            subtitle: transcript
              ? "Succesfully Uploaded"
              : q.accessRestricted
              ? "Transcript provided, but access has been restricted due to outstanding fees"
              : "Full transcript required that lists your individual subjects and marks",
            file: transcript,
            api: {
              post: `/candidate/profile_qualifications/${q.id}/transcript`,
              get: "candidate/profileQualifications/fetchAll",
            },
            flag: this.flags.find(
              (f) =>
                f.targetType === "ProfileQualification" && f.targetId === q.id
            ),
            clearable: true,
          });
          continue;
        }

        // Else proof of registration
        let registration = _.get(q, "proofOfRegistration");

        this.documents.push({
          icon: registration ? "mdi-file" : "mdi-file-outline",
          color: registration
            ? this.themes.candidate.light.success
            : this.themes.candidate.light.warning,
          title: `${q.qualificationTypeName} ${q.specialization}`,
          subtitle: registration
            ? "Succesfully Uploaded"
            : "Proof of Registration Required",
          file: registration,
          api: {
            post: `/candidate/profile_qualifications/${q.id}/proof_of_registration`,
            get: "candidate/profileQualifications/fetchAll",
          },
          clearable: true,
        });
      }

      if (_.get(this, "profile.financial.incomes[0]")) {
        this.documents.push({ header: "Household Income" });
        this.profile.financial.incomes.forEach((i) => {
          this.documents.push({
            icon: i.proof ? "mdi-file" : "mdi-file-outline",
            color: i.proof
              ? this.themes.candidate.light.success
              : this.themes.candidate.light.warning,
            title: `${i.relationship}: ${i.typeOfProof}`,
            file: i.proof,
            api: {
              post: `/candidate/financial_survey/household_incomes/${i.id}/proof`,
              get: "candidate/profile/fetch",
            },
            flag: this.flags.find(
              (f) => f.targetType === "HouseholdIncome" && f.targetId === i.id
            ),
          });
        });
      }
    },

    levelsCountryMode() {
      let qualifications = JSON.parse(
        JSON.stringify(this.profileQualifications)
      );
      const countries = qualifications
        .filter((pq) => _.get(pq, "qualification.levels[0]"))
        .reduce(
          (acc, { qualification: { levels } }) =>
            acc.concat(levels.map((l) => l.countryCode)),
          []
        );
      return countries[0] ? mode(countries) : null;
    },

    moreThanSixMonthsAgo(date) {
      if (!date) return false;

      const dateA = new Date(date);
      let SixMonthsAgo = new Date();
      SixMonthsAgo.setMonth(SixMonthsAgo.getMonth() - 6);

      return dateA < SixMonthsAgo;
    },
  },
};
</script>
